import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';
import { LightColors, DarkColors, ColorScheme } from '../theme/colors';
import { FontFamily, FontSize, FontWeight, LineHeight } from '../theme/typography';
import { Spacing } from '../theme/spacing';
import { Radii } from '../theme/radii';
import { Shadows } from '../theme/shadows';
import { Motion } from '../theme/motion';

// Theme context — mirrors sk-catalog's shape (stack convention). Values come from
// theme/*, which carry the SK Voice HTML's monochrome palette + tokens.
interface Theme {
  colors: ColorScheme;
  font: typeof FontFamily;
  fontSize: typeof FontSize;
  fontWeight: typeof FontWeight;
  lineHeight: typeof LineHeight;
  spacing: typeof Spacing;
  radii: typeof Radii;
  shadows: typeof Shadows;
  motion: typeof Motion;
  isDark: boolean;
}

interface ThemeContextValue {
  theme: Theme;
  isDark: boolean;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function buildTheme(isDark: boolean): Theme {
  return {
    colors: isDark ? DarkColors : LightColors,
    font: FontFamily,
    fontSize: FontSize,
    fontWeight: FontWeight,
    lineHeight: LineHeight,
    spacing: Spacing,
    radii: Radii,
    shadows: Shadows,
    motion: Motion,
    isDark,
  };
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    AsyncStorage.getItem('theme').then(val => {
      if (val === 'dark') {
        setIsDark(true);
        if (Platform.OS === 'web') document.documentElement.setAttribute('data-theme', 'dark');
      }
    });
  }, []);

  const toggleTheme = useCallback(() => {
    setIsDark(d => {
      const next = !d;
      AsyncStorage.setItem('theme', next ? 'dark' : 'light');
      if (Platform.OS === 'web') {
        document.documentElement.setAttribute('data-theme', next ? 'dark' : 'light');
      }
      return next;
    });
  }, []);

  return (
    <ThemeContext.Provider value={{ theme: buildTheme(isDark), isDark, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}

const fallback: ThemeContextValue = { theme: buildTheme(false), isDark: false, toggleTheme: () => {} };

export function useThemeSafe(): ThemeContextValue {
  return useContext(ThemeContext) ?? fallback;
}
